import { useState } from "react";
import { setSerchedRides } from "store/slices/dataSlice";
import { useNavigate } from "react-router-dom";
import { searchRidesApi } from 'services/auth/rideApis';
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "@/components/ui/calendar.js";
import MapAutoComplete from "@/components/MapAutoComplete";
import { FiMapPin, FiCalendar, FiUserCheck, FiUser, FiSearch, FiMessageCircle, FiSmile, FiChevronDown } from "react-icons/fi";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export const SearchRide = () => {
  const navigator = useNavigate();
  const [date, setDate] = useState();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateStr = date ? format(date, "yyyy-MM-dd") : "";
    navigator(`/ride-details/search?from=${from}&to=${to}&date=${dateStr}`);
  };

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex flex-col items-center min-h-screen w-full z-10">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] w-full py-8 px-4 backdrop-blur relative">
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full max-w-md space-y-5 flex flex-col justify-center border border-zinc-100"
          style={{ minHeight: 'fit-content' }}
        >
          <div className="text-center space-y-1">
            <h2 className="text-2xl font-bold text-zinc-900">Search for a ride</h2>
            <p className="text-amber-600 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-md inline-block text-sm font-medium"><strong>Note:</strong> Leave fields blank to view all available rides.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="from" className="flex items-center gap-2 mb-1.5 text-zinc-700 font-medium"><FiMapPin className="w-4 h-4 text-amber-500" /> From</Label>
              <MapAutoComplete value={from} changeHandler={setFrom} placeholder={"Enter starting location"} />
            </div>
            <div>
              <Label htmlFor="to" className="flex items-center gap-2 mb-1.5 text-zinc-700 font-medium"><FiMapPin className="w-4 h-4 text-amber-500" /> To</Label>
              <MapAutoComplete value={to} changeHandler={setTo} placeholder={"Enter destination"} />
            </div>
            <div>
              <Label htmlFor="date" className="flex items-center gap-2 mb-1.5 text-zinc-700 font-medium"><FiCalendar className="w-4 h-4 text-amber-500" /> Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={"w-full justify-start text-left font-normal border-zinc-200" + (!date ? " text-zinc-400" : " text-zinc-800")}
                    type="button"
                  >
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(day) => day < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Button type="submit" className="w-full bg-zinc-900 text-white hover:bg-amber-500 hover:text-zinc-900 transition-colors duration-300 font-semibold py-6 text-lg rounded-xl mt-2" >
            Search
          </Button>
        </form>

        {/* Scroll Indicator */}
        <button 
          type="button"
          onClick={scrollToHowItWorks}
          className="absolute bottom-8 flex flex-col items-center text-zinc-800 hover:text-amber-600 transition-colors animate-bounce font-semibold bg-white/80 px-5 py-2.5 rounded-full backdrop-blur-md shadow-lg border border-zinc-200"
        >
          <span className="text-sm mb-1">How it works</span>
          <FiChevronDown className="w-5 h-5" />
        </button>
      </div>

      {/* How It Works Section */}
      <div id="how-it-works" className="w-full bg-white py-24 px-4 md:px-8 border-t border-zinc-100 shadow-2xl">
        <div className="max-w-6xl mx-auto space-y-20">
          
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-5xl font-extrabold text-zinc-900 tracking-tight">How to use <span className="text-amber-500">Travel Buddy</span></h2>
            <p className="text-zinc-500 max-w-2xl mx-auto text-lg leading-relaxed">A community ride-matching platform exclusively for MUJ students and faculty. Anyone can host or join a ride — no vehicle required.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center space-y-4 p-6 rounded-3xl bg-zinc-50 border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-white shadow-sm text-amber-500 border border-zinc-100 rounded-2xl flex items-center justify-center rotate-3 hover:rotate-0 transition-transform">
                <FiUserCheck className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">1. Sign Up</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Register using your official MUJ email ID. You are automatically verified as a trusted member.</p>
            </div>
            {/* Step 2 */}
            <div className="flex flex-col items-center text-center space-y-4 p-6 rounded-3xl bg-zinc-50 border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-white shadow-sm text-amber-500 border border-zinc-100 rounded-2xl flex items-center justify-center -rotate-3 hover:rotate-0 transition-transform">
                <FiUser className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">2. Profile</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Add your name and usual commute area. Just enough for others to know you.</p>
            </div>
            {/* Step 3 */}
            <div className="flex flex-col items-center text-center space-y-4 p-6 rounded-3xl bg-zinc-50 border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-white shadow-sm text-amber-500 border border-zinc-100 rounded-2xl flex items-center justify-center rotate-3 hover:rotate-0 transition-transform">
                <FiSearch className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">3. Host or Join</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Post your route and cost, or browse requests that match your timing.</p>
            </div>
            {/* Step 4 */}
            <div className="flex flex-col items-center text-center space-y-4 p-6 rounded-3xl bg-zinc-50 border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-white shadow-sm text-amber-500 border border-zinc-100 rounded-2xl flex items-center justify-center -rotate-3 hover:rotate-0 transition-transform">
                <FiMessageCircle className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">4. Connect</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Once accepted, both receive each other's WhatsApp to sort out the pickup spot.</p>
            </div>
            {/* Step 5 */}
            <div className="flex flex-col items-center text-center space-y-4 p-6 rounded-3xl bg-zinc-50 border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-white shadow-sm text-amber-500 border border-zinc-100 rounded-2xl flex items-center justify-center rotate-3 hover:rotate-0 transition-transform">
                <FiSmile className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">5. Show Up</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Travel with verified members, split the fare, and enjoy a stress-free commute.</p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto pt-4">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-8 text-center tracking-tight">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full bg-zinc-50/50 rounded-3xl border border-zinc-100 px-6 py-4 shadow-sm">
              <AccordionItem value="item-1" className="border-b border-zinc-200/60 last:border-0 py-2">
                <AccordionTrigger className="text-left font-semibold text-lg text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline">Do I need to own a vehicle?</AccordionTrigger>
                <AccordionContent className="text-zinc-600 text-base leading-relaxed pt-2 pb-4">
                  <em>No vehicle? No problem!</em> Whether you book a cab, an auto, or any other mode of transport, you can still host or join a ride.<br/>
                  And <em>if you own a vehicle</em>, you can host rides with it too.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b border-zinc-200/60 last:border-0 py-2">
                <AccordionTrigger className="text-left font-semibold text-lg text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline">Who can sign up?</AccordionTrigger>
                <AccordionContent className="text-zinc-600 text-base leading-relaxed pt-2 pb-4">
                  Any student or faculty member with a valid MUJ email ID. The platform is exclusive to the MUJ community.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b border-zinc-200/60 last:border-0 py-2">
                <AccordionTrigger className="text-left font-semibold text-lg text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline">How is the cost determined?</AccordionTrigger>
                <AccordionContent className="text-zinc-600 text-base leading-relaxed pt-2 pb-4">
                  The host sets the per-head cost upfront when creating the ride request. You see the exact amount before sending a join request — no surprises.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-b border-zinc-200/60 last:border-0 py-2">
                <AccordionTrigger className="text-left font-semibold text-lg text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline">Is it safe to ride with strangers?</AccordionTrigger>
                <AccordionContent className="text-zinc-600 text-base leading-relaxed pt-2 pb-4">
                  Every user is verified through their MUJ credentials, so you're always riding within a trusted university network.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-b border-zinc-200/60 last:border-0 py-2">
                <AccordionTrigger className="text-left font-semibold text-lg text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline">What if the host cancels the ride?</AccordionTrigger>
                <AccordionContent className="text-zinc-600 text-base leading-relaxed pt-2 pb-4">
                  If a ride is cancelled, you'll be notified and can browse other available requests heading your way.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SearchRide;
